import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/Components/ui/card';
import { Button } from '@/Components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/Components/ui/dialog';
import { Trash2, Power, PowerOff, RefreshCw, AlertTriangle, CheckCircle, XCircle, Clock, Users, LogOut } from 'lucide-react';

interface Tenant {
  tenantId: string;
  companyName: string;
  databaseName: string;
  createdDate: string;
  isActive: boolean;
  provisioningStatus: number;
  provisioningError: string | null;
  provisioningCompletedDate: string | null;
  planType: string;
  subscriptionEndDate: string | null;
}

const ProvisioningStatusLabels: Record<number, { label: string; color: string; icon: JSX.Element }> = {
  0: { label: 'Pending', color: 'text-gray-500', icon: <Clock className="h-4 w-4" /> },
  1: { label: 'Creating Database', color: 'text-blue-500', icon: <RefreshCw className="h-4 w-4 animate-spin" /> },
  2: { label: 'Applying Migrations', color: 'text-blue-500', icon: <RefreshCw className="h-4 w-4 animate-spin" /> },
  3: { label: 'Creating Company', color: 'text-blue-500', icon: <RefreshCw className="h-4 w-4 animate-spin" /> },
  4: { label: 'Creating User', color: 'text-blue-500', icon: <RefreshCw className="h-4 w-4 animate-spin" /> },
  5: { label: 'Finalizing', color: 'text-blue-500', icon: <RefreshCw className="h-4 w-4 animate-spin" /> },
  6: { label: 'Ready', color: 'text-green-600', icon: <CheckCircle className="h-4 w-4" /> },
  7: { label: 'Failed', color: 'text-red-600', icon: <XCircle className="h-4 w-4" /> },
};

export default function TenantManagement() {
  const navigate = useNavigate();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchTenants();
  }, []);

  const fetchTenants = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/Admin/tenants');
      if (response.ok) {
        const data = await response.json();
        setTenants(data);
      } else {
        setError('Failed to load tenants');
      }
    } catch (err) {
      console.error('Error fetching tenants:', err);
      setError('Network error while loading tenants');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (tenant: Tenant) => {
    setSelectedTenant(tenant);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!selectedTenant) return;

    try {
      setDeleting(true);
      const response = await fetch(`/api/Admin/tenants/${selectedTenant.tenantId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        const data = await response.json();
        alert(data.message);
        setDeleteDialogOpen(false);
        setSelectedTenant(null);
        fetchTenants(); // Refresh the list
      } else {
        const errorData = await response.json();
        alert(`Error: ${errorData.message}`);
      }
    } catch (err) {
      console.error('Error deleting tenant:', err);
      alert('Network error while deleting tenant');
    } finally {
      setDeleting(false);
    }
  };

  const handleToggleActive = async (tenant: Tenant) => {
    const action = tenant.isActive ? 'deactivate' : 'activate';
    try {
      const response = await fetch(`/api/Admin/tenants/${tenant.tenantId}/${action}`, {
        method: 'PUT',
      });

      if (response.ok) {
        fetchTenants(); // Refresh the list
      } else {
        const errorData = await response.json();
        alert(`Error: ${errorData.message}`);
      }
    } catch (err) {
      console.error(`Error ${action}ing tenant:`, err);
      alert(`Network error while ${action}ing tenant`);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleLogout = async () => {
    try {
      await fetch('/Account/logout', {
        method: 'POST',
        credentials: 'include',
      });
      // Redirect to login page
      window.location.href = '/';
    } catch (err) {
      console.error('Error logging out:', err);
      alert('Failed to logout');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle className="text-3xl">Tenant Management</CardTitle>
              <CardDescription>Manage PPMeasy tenants and their databases</CardDescription>
            </div>
            <div className="flex gap-2">
              <Button onClick={fetchTenants} variant="outline">
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
              <Button onClick={handleLogout} variant="destructive">
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}

          <div className="space-y-4">
            {tenants.map((tenant) => (
              <Card key={tenant.tenantId} className={!tenant.isActive ? 'opacity-60' : ''}>
                <CardContent className="pt-6">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-semibold">{tenant.companyName}</h3>
                        <span className={`flex items-center gap-1 text-sm ${ProvisioningStatusLabels[tenant.provisioningStatus].color}`}>
                          {ProvisioningStatusLabels[tenant.provisioningStatus].icon}
                          {ProvisioningStatusLabels[tenant.provisioningStatus].label}
                        </span>
                        {!tenant.isActive && (
                          <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full">
                            Inactive
                          </span>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 text-sm text-muted-foreground">
                        <div>
                          <span className="font-medium">Tenant ID:</span> {tenant.tenantId}
                        </div>
                        <div>
                          <span className="font-medium">Database:</span> {tenant.databaseName}
                        </div>
                        <div>
                          <span className="font-medium">Created:</span> {formatDate(tenant.createdDate)}
                        </div>
                        <div>
                          <span className="font-medium">Plan:</span> {tenant.planType}
                        </div>
                        {tenant.subscriptionEndDate && (
                          <div>
                            <span className="font-medium">Subscription Ends:</span> {formatDate(tenant.subscriptionEndDate)}
                          </div>
                        )}
                        {tenant.provisioningCompletedDate && (
                          <div>
                            <span className="font-medium">Provisioned:</span> {formatDate(tenant.provisioningCompletedDate)}
                          </div>
                        )}
                      </div>

                      {tenant.provisioningError && (
                        <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
                          <div className="flex items-start gap-2">
                            <AlertTriangle className="h-4 w-4 mt-0.5" />
                            <div>
                              <div className="font-medium">Provisioning Error:</div>
                              <div>{tenant.provisioningError}</div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/admin/tenants/${tenant.tenantId}/users`)}
                        title="Manage users for this tenant"
                      >
                        <Users className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleToggleActive(tenant)}
                        title={tenant.isActive ? 'Deactivate tenant' : 'Activate tenant'}
                      >
                        {tenant.isActive ? <PowerOff className="h-4 w-4" /> : <Power className="h-4 w-4" />}
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDeleteClick(tenant)}
                        title="Delete tenant permanently"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}

            {tenants.length === 0 && !loading && (
              <div className="text-center py-12 text-muted-foreground">
                No tenants found
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Delete Tenant - Permanent Action
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="mb-4">
              Are you sure you want to permanently delete <strong>{selectedTenant?.companyName}</strong>?
            </p>
            <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-700">
              <strong>⚠️ Warning:</strong> This action cannot be undone. This will:
              <ul className="list-disc ml-5 mt-2">
                <li>Delete the tenant record</li>
                <li>Delete all user mappings</li>
                <li>Delete all subscriptions</li>
                <li>Drop the database: <code className="bg-red-100 px-1 rounded">{selectedTenant?.databaseName}</code></li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              disabled={deleting}
            >
              {deleting ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Permanently
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
